"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { createEmployeeUtilization, getWorkCategories, getMyUtilizations } from "api/apiHelper"
import { WorkCategory, CreateUtilizationRequest, EmployeeUtilization}  from "types/employee-utilization"
import { getMonday, getSunday } from "utils/utilityFunctions";

import {toast} from "react-toastify"
import { useIsManager, usePermissions } from 'app/hooks';


interface UtilizationEntry {
  id: string
  date: string
  hours: number
  category: number
}

const EmployeeUtilizationEntry: React.FC = () => {
  const [entries, setEntries] = useState<UtilizationEntry[]>([{ id: "1", date: "", hours: 0, category: 0 }])
  const [savedUtilizations, setSavedUtilizations] = useState<EmployeeUtilization[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [activeFilter, setActiveFilter] = useState<"week" | "month" | "custom">("week")
  const [workCategories, setWorkCategories] = useState<WorkCategory[]>([])
  const [filteredUtilizations, setFilteredUtilizations] = useState<EmployeeUtilization[]>([])
  const isManager = useIsManager();
  const permissions = usePermissions() || [];
  

  const navigate = useNavigate()
  

  useEffect(() => {
    const getFilteredUtilizations = () => {
      const filtered = savedUtilizations.filter(entry => {
        const entryDate = new Date(entry.date)
        const startDateObj = new Date(startDate)
        const endDateObj = new Date(endDate)
        return entryDate >= startDateObj && entryDate <= endDateObj
      })
      setFilteredUtilizations(filtered)
    }

    if (startDate && endDate) {
      getFilteredUtilizations()
      setActiveFilter("custom")
    }
  }, [startDate, endDate, savedUtilizations]
  )

  // Fetch work categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await getWorkCategories();
        setWorkCategories(categories);
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    };
  
    fetchCategories();
  }, []);

  // Fetch saved utilizations on component mount
  useEffect(() => {
    const fetchUtilizations = async () => {
      try {
        const utilizations = await getMyUtilizations();
        setSavedUtilizations(utilizations);
      } catch (error) {
        console.error("Failed to load utilizations:", error);
      }
    };

    fetchUtilizations();
  }, []);

  // Initialize date range to current week
  useEffect(() => {
    const monday = getMonday(new Date())
    const sunday = getSunday(monday)
    setStartDate(monday.toISOString().split("T")[0])
    setEndDate(sunday.toISOString().split("T")[0])
  }, [])

  const addNewEntry = () => {
    const newEntry: UtilizationEntry = {
      id: Date.now().toString(),
      date: "",
      hours: 0,
      category: 0,
    }
    setEntries([...entries, newEntry])
  }

  const removeEntry = (id: string) => {
    if (entries.length > 1) {
      setEntries(entries.filter((entry) => entry.id !== id))
    }
  }

  const updateEntry = (id: string, field: keyof UtilizationEntry, value: string | number) => {
    setEntries(entries.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)))
  }

  const saveEntries = async () => {
    // Validate entries
    const validEntries = entries.filter((entry) => entry.date && entry.hours > 0 && entry.category)
    if (validEntries.length === 0) {
      toast.error("Please fill in at least one complete entry")
      return
    }
  
    setIsLoading(true)
    try {
      // Group entries by date to create proper utilization requests
      const entriesByDate = validEntries.reduce((acc, entry) => {
        if (!acc[entry.date]) {
          acc[entry.date] = []
        }
        acc[entry.date].push(entry)
        return acc
      }, {} as Record<string, UtilizationEntry[]>)
  
      // Create utilization requests for each date
      const requests: CreateUtilizationRequest[] = Object.entries(entriesByDate).map(([date, dateEntries]) => ({
        date,
        items: dateEntries.map(entry => ({
          type: entry.category,
          utilization_hours: entry.hours
        }))
      }))

      // Call the API for each request
      const responses = await Promise.all(
        requests.map((request) => createEmployeeUtilization(request))
      )
      // Fetch fresh data after saving
      const updatedUtilizations = await getMyUtilizations()
      setSavedUtilizations(updatedUtilizations)

      // Reset form entries
      setEntries([{ id: Date.now().toString(), date: "", hours: 0, category: 0 }])
      toast.success(`Successfully saved ${validEntries.length} utilization entries`)
    } catch (error) {
      console.error(error)
      toast.error("Failed to save entries. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleShowThisWeek = () => {
    const monday = getMonday(new Date())
    const sunday = getSunday(monday)
    setStartDate(monday.toISOString().split("T")[0])
    setEndDate(sunday.toISOString().split("T")[0])
    setActiveFilter("week")
  }

  const handleShowThisMonth = () => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    setStartDate(firstDay.toISOString().split("T")[0])
    setEndDate(lastDay.toISOString().split("T")[0])
    setActiveFilter("month")
  }

  const getHoursColorClass = (hours: number) => {
    if (hours < 6) {
      return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
    } else if (hours >= 6 && hours <= 7) {
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
    } else if (hours === 8) {
      return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
    } else {
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
      case "approved":
        return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
      case "declined":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "Pending"
      case "approved":
        return "Manager Approved"
      case "declined":
        return "Manager Declined"
      default:
        return status
    }
  }

  // Update getTotalHours to only include approved entries
  const getTotalHours = () => {
    const approvedUtilizations = filteredUtilizations.filter(
      entry => entry.status === 'approved'
    );
    
    return approvedUtilizations.reduce((total, entry) => 
      total + (Number(entry.total_hours) || 0), 0
    ).toFixed(1);
  };



  // Update getAverageHours to only include approved entries
  const getAverageHours = () => {
    const approvedUtilizations = filteredUtilizations.filter(
      entry => entry.status === 'approved'
    );
    
    if (approvedUtilizations.length === 0) return "0.0";
    
    const total = approvedUtilizations.reduce((sum, entry) => 
      sum + (Number(entry.total_hours) || 0), 0
    );
    
    return (total / approvedUtilizations.length).toFixed(1);
  };
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Entry Form */}
      {permissions && permissions.includes("employee_utilization.add_employeeutilization") && (

        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
                <i className="fas fa-plus text-brand-600 dark:text-brand-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-navy-700 dark:text-white">Add Utilization Entries</h3>
            </div>
            <div className="flex items-center justify-end">
              {(isManager || permissions.includes("employee_utilization.view_employeeutilization")) && (
                <button
                  type="button"
                  onClick={() => navigate("/employee-utilization/employees")}
                  className="px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-all mr-4"
                >
                  <i className="fas fa-tasks mr-2"></i>
                  Manage Utilizations
                </button>
              )}
              <button
                type="button"
                onClick={saveEntries}
                disabled={isLoading}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all disabled:opacity-50"
              >
                <i className="fas fa-save mr-2"></i>
                {isLoading ? "Saving..." : "Save All"}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {entries.map((entry, index) => (
              <div key={entry.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-navy-700/30 rounded-lg">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
                    <div className="relative">
                      <input
                        type="date"
                        value={entry.date}
                        onChange={(e) => updateEntry(entry.id, "date", e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      />
                      <i className="fas fa-calendar absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Hours</label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="24"
                        step="1"
                        value={entry.hours || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow integers, reject decimal values
                          if (value === '' || /^\d+$/.test(value)) {
                            updateEntry(entry.id, "hours", parseInt(value) || 0);
                          }
                        }}
                        onKeyPress={(e) => {
                          // Prevent decimal point entry
                          if (e.key === '.' || e.key === ',') {
                            e.preventDefault();
                          }
                        }}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        placeholder="0"
                      />
                      <i className="fas fa-clock absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                    <div className="relative">
                      <select
                        value={entry.category}
                        onChange={(e) => updateEntry(entry.id, "category", Number(e.target.value))}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all appearance-none"
                      >
                        <option value="">Select category</option>
                        {(workCategories || []).map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.type}</option>
                        ))}
                      </select>
                      <i className="fas fa-tag absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-6">
                  {index === entries.length - 1 && (
                    <button
                      type="button"
                      onClick={addNewEntry}
                      className="p-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-all"
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  )}

                  {entries.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEntry(entry.id)}
                      className="p-3 border border-red-300 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-calendar-alt text-brand-600 dark:text-brand-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">View Saved Records</h3>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  />
                  <i className="fas fa-calendar absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  />
                  <i className="fas fa-calendar absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => setActiveFilter("custom")}
                  className=" px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-all"
                >
                  <i className="fas fa-search mr-2"></i>
                  Apply Filter
                </button>
              </div>
            </div>
          </div>

          <div className="lg:w-80">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Filters</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleShowThisWeek}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeFilter === "week"
                    ? "bg-brand-500 text-white"
                    : "bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                }`}
              >
                <i className="fas fa-calendar-week mr-1"></i>
                This Week
              </button>

              <button
                type="button"
                onClick={handleShowThisMonth}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeFilter === "month"
                    ? "bg-brand-500 text-white"
                    : "bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                }`}
              >
                <i className="fas fa-calendar-alt mr-1"></i>
                This Month
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Hours</p>
              <p className="text-3xl font-bold text-navy-700 dark:text-white">{getTotalHours()}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-clock text-xl text-emerald-600 dark:text-emerald-400"></i>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Entries</p>
              <p className="text-3xl font-bold text-navy-700 dark:text-white">{filteredUtilizations.length}</p>
            </div>
            <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-list text-xl text-purple-600 dark:text-purple-400"></i>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6 border border-gray-100 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Avg Hours</p>
              <p className="text-3xl font-bold text-navy-700 dark:text-white">{getAverageHours()}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-center">
              <i className="fas fa-chart-line text-xl text-orange-600 dark:text-orange-400"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Records Table */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-navy-700 bg-gray-50 dark:bg-navy-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
                <i className="fas fa-table text-brand-600 dark:text-brand-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-navy-700 dark:text-white">My Utilization Records</h3>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {startDate &&
                endDate &&
                `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="max-h-[500px] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-navy-700/30">
                  <th className="px-4 py-3 text-left font-semibold text-navy-700 dark:text-white">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-calendar text-xs"></i>
                      <span>Date</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-tag text-xs"></i>
                      <span>Categories</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">
                    <div className="flex items-center justify-center gap-2">
                      <i className="fas fa-clock text-xs"></i>
                      <span>Total Hours</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-navy-700 dark:text-white">
                    <div className="flex items-center justify-center gap-2">
                      <i className="fas fa-info-circle text-xs"></i>
                      <span>Status</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUtilizations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-navy-700 rounded-full flex items-center justify-center">
                          <i className="fas fa-inbox text-2xl text-gray-400"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-navy-700 dark:text-white mb-1">No Records Found</h3>
                          <p className="text-gray-500 dark:text-gray-400">
                            No utilization records found for the selected date range.
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUtilizations.map((entry, index) => (
                    <tr
                      key={entry.id}
                      className={`border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50 dark:hover:bg-navy-700/30 transition-colors ${
                        index % 2 === 0 ? "bg-white dark:bg-navy-800" : "bg-gray-50/50 dark:bg-navy-700/20"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-navy-700 dark:text-white">
                        {new Date(entry.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(entry.items || []).map((item, idx) => (
                            <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400">
                              {item.type_name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center justify-center w-16 h-6 rounded-md font-medium text-xs ${getHoursColorClass(entry.total_hours)}`}
                        >
                          {entry.total_hours}h
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center justify-center px-3 py-1 rounded-full font-medium text-xs ${getStatusColor(entry.status)}`}
                        >
                          {getStatusText(entry.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployeeUtilizationEntry